import { cloneElement, useId, type ComponentProps, type ReactElement, type ReactNode } from "react";
import { Input as PrimitiveInput } from "../primitives/input";
import { Textarea as PrimitiveTextarea } from "../primitives/textarea";
import { useDesignSystem } from "./DesignSystem";
import "./controls.css";

export function Input({ className = "", ...props }: ComponentProps<typeof PrimitiveInput>) {
  const themed = useDesignSystem();
  const controlClass = `ui-input ${className}`.trim();
  /* 수량 칸에는 브라우저 자동 완성(지난 입력 목록)을 띄우지 않는다 — 수량은 매번 새로 세는 값이라 옛 값 목록이 칸을 덮기만 했다(2026-10-21 지윤). */
  const next = props.inputMode === "numeric" && props.autoComplete === undefined ? { ...props, autoComplete: "off" } : props;
  return themed ? <PrimitiveInput {...next} className={controlClass} /> : <input {...next} className={controlClass} />;
}

export function Textarea({ className = "", ...props }: ComponentProps<typeof PrimitiveTextarea>) {
  const themed = useDesignSystem();
  const controlClass = `ui-input ${className}`.trim();
  return themed ? <PrimitiveTextarea {...props} className={controlClass} /> : <textarea {...props} className={controlClass} />;
}

type FieldControl = { id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling" };

/** Explicit label/control association also works with the custom Select's form bridge. */
export function Field({ label, children, hint, error, required, className = "" }: {
  label: ReactNode; children: ReactElement<FieldControl>; hint?: ReactNode; error?: ReactNode; required?: boolean; className?: string;
}) {
  const generated = useId();
  const id = children.props.id ?? generated;
  const describedBy = [children.props["aria-describedby"], hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ");
  return <div className={`ui-field ${className}`.trim()}>
    {/* 필수에 별표를 붙이지 않는다. 이 앱의 폼은 거의 모든 칸이 필수라 별표가 줄마다 붙어
        아무것도 구별하지 못했다. 규칙을 뒤집어 선택인 칸에만 "선택"이라 적는다 — 표시가
        붙는 칸이 적을수록 그 표시가 실제로 눈에 들어온다. */}
    <label htmlFor={id}>{label}{!required && <span className="ui-field-optional ui-field-optional--mark"> 선택</span>}</label>
    {cloneElement(children, { id, "aria-describedby": describedBy || undefined, "aria-invalid": error ? true : children.props["aria-invalid"] })}
    {hint && <small id={`${id}-hint`} className="ui-field-hint">{hint}</small>}
    {error && <span id={`${id}-error`} className="ui-field-error" role="alert">{error}</span>}
  </div>;
}
